import React from 'react'
import { Container, Content } from './styles'
import { QRCodeSVG } from 'qrcode.react'
import ModalDelete from '../../../../components/Modals/ModalDelete'
import { SideBarStyles } from '../../Logs/SideBarLogs/styles'
import ClickAwayListener from 'react-click-away-listener'
import { useForm } from 'react-hook-form'
import Input from '../../../../components/Input'
import { format } from 'date-fns'

import GroupsSearch from './GroupsSearch'
import { Document } from '../../../../services/documents.service'
import { api } from '../../../../services/api'
import { formatFileSize } from '../../../../utils/utils'
import useToast from '../../../../hooks/useToast'
import { getFavorites } from '../../../../services/utils'
import { useUserStore } from '../../../../store/user'
import { usePermissions } from '../../../../hooks/usePermissions'
import {
  FaCalendar,
  FaCheck,
  FaHeart,
  FaPencil,
  FaRegHeart,
  FaTrash,
  FaXmark,
} from 'react-icons/fa6'
import { ButtonIcon } from '../../../../components/Table/Pagination/styles'
import { useTheme } from 'styled-components'
import { SelectDocumentType } from '../../../../components/Modals/ModalUpload'
import { Content as ContentInput, Field, Label } from '../../../../components/Input/styles'
import { ButtonRound } from '../../../../components/Header/styles'
import { ResponseError } from '../../../../validators/utils.interface'
import axios from 'axios'

export type ModalDeleteRef = React.ElementRef<typeof ModalDelete>

export interface FileDetailsProps {
  refetch?: () => void
}

export interface ModalHandle {
  openModal: (_id: string) => void
  closeModal: () => void
}

const FileDetails: React.ForwardRefRenderFunction<ModalHandle, FileDetailsProps> = (
  { refetch },
  ref,
) => {
  const [open, setOpen] = React.useState(false)
  const { user } = useUserStore()
  const { canEditDocuments, canDeleteDocuments } = usePermissions()

  // Verificar permissões baseadas em grupo ao invés de role do usuário
  const canEdit = canEditDocuments
  const canDelete = canDeleteDocuments
  const [isLoading, setIsLoading] = React.useState(false)
  const [fileDetails, setFileDetails] = React.useState<Document>()
  const [openTimeout, setOpenTimeout] = React.useState(false)

  const [edit, setEdit] = React.useState(false)
  const [metadata, setMetadata] = React.useState([])

  const toast = useToast()
  const theme: any = useTheme()

  const modalDeleteRef = React.useRef<ModalDeleteRef>(null)

  const {
    control,
    handleSubmit,
    setValue,
    setFocus,
    watch,
    getValues,
    formState: { errors },
  } = useForm<Document>({
    defaultValues: {
      name: '',
      createdAt: '',
      size: '',
      owner: '',
      documentType: '',
      isFolder: false
    }
  })

  const handleGetData = async (_id?: string, refreshSim = false) => {
    try {
      setIsLoading(true)
      const { data: response } = await api.get(`/documents/${_id}${refreshSim ? '?refreshSim=true' : ''}`)

      // Os dados do documento estão em response.document, não diretamente em response
      let documentData: any = response.document || response

      setFileDetails(documentData)
      
      // Debug: verificar estrutura do owner
      console.log('Dados do documento recebidos:', {
        owner: documentData?.owner,
        ownerType: typeof documentData?.owner,
        ownerName: documentData?.owner?.name,
        fullDocument: documentData
      })
      
      setValue('documentType', documentData?.documentTypeWithData?.documentType?.name || '')
      if (documentData?.documentTypeWithData) {
        let docMetadata = documentData.documentTypeWithData.metadata || [];
        const schemaMetadata = documentData.documentTypeWithData.documentType?.metadata || [];
        
        if (docMetadata.length === 0 && schemaMetadata.length > 0) {
           docMetadata = schemaMetadata.map((m: any) => ({ name: m.name, value: 'Não informado' }));
        }
        setMetadata(docMetadata)
      }
      setValue('name', documentData.name || '')
      
      // Verificar se a data é válida antes de formatar
      if (documentData.createdAt) {
        try {
          const date = new Date(documentData.createdAt)
          if (!isNaN(date.getTime())) {
            setValue('createdAt', format(date, 'dd/MM/yyyy HH:mm'))
          } else {
            setValue('createdAt', 'Data inválida')
          }
        } catch (error) {
          console.error('Erro ao formatar data:', error)
          setValue('createdAt', 'Data inválida')
        }
      } else {
        setValue('createdAt', 'Data não disponível')
      }
      
      setValue('isFolder', documentData.isFolder)
      if (!documentData.isFolder && documentData.size) {
        let size = formatFileSize(Number(documentData.size))
        setValue('size', size)
      }
      
      // Tratar owner: pode vir como objeto {name: "..."}, string (ID) ou null
      let ownerName = ''
      if (documentData?.owner) {
        if (typeof documentData.owner === 'object' && documentData.owner.name) {
          // Owner está populado como objeto
          ownerName = documentData.owner.name
        } else if (typeof documentData.owner === 'string') {
          // Owner é apenas o ID, não foi populado - deixar vazio ou buscar
          ownerName = ''
          console.warn('Owner retornado como ID string, não foi populado:', documentData.owner)
        }
      }
      setValue('owner', ownerName)
      
      // Log para verificar se os valores foram definidos
      setTimeout(() => {
        console.log('Valores definidos no formulário:', {
          name: getValues('name'),
          documentType: getValues('documentType'),
          owner: getValues('owner'),
          createdAt: getValues('createdAt'),
          size: getValues('size'),
          isFolder: getValues('isFolder')
        })
        setOpenTimeout(true)
      }, 500)
    } catch (error) {
      console.error('Erro ao carregar dados do documento:', error)
      toast.errorToast('Erro ao carregar informações do documento')
    } finally {
      setIsLoading(false)
    }
  }

  const onEditName = async () => {
    setEdit(true)

    setFocus('name')
  }

  const handleEditName = async () => {
    setEdit(true)

    const name = getValues('name')
    handleEdit({
      name,
    })
    setEdit(false)
  }

  const handleSubmitDelete = async (_id: string) => {
    try {
      await api.delete(`/documents/${_id}`)

      toast.successToast('Arquivo deletado com sucesso!')
      if (refetch) refetch()
      handleClickAway()
    } catch (error) {
      const typedError = error as ResponseError
      if (axios.isAxiosError(error)) {
        toast.errorToast(typedError?.response?.data?.message)
      } else {
        toast.errorToast('Erro ao deletar o registro !')
      }
    } finally {
      modalDeleteRef.current?.closeModal()
    }
  }

  const handleEdit = async (formData: any) => {
    try {
      await api.put(`/documents/${fileDetails?._id}`, formData)

      toast.successToast('Arquivo editado com sucesso!')
      handleGetData(fileDetails?._id)
      if (refetch) refetch()
      getFavorites()
    } catch (error) {}
  }

  const handleClickAway = () => {
    setOpen(false)
    setOpenTimeout(false)
    setFileDetails(undefined)
  }

  React.useImperativeHandle(ref, () => ({
    openModal(_id) {
      setOpen(true)
      setMetadata([])
      handleGetData(_id)
    },
    closeModal() {
      setOpen(false)
    },
  }))

  const name = watch('name')

  return (
    <Container open={open}>
      <Content>
        <SideBarStyles.Header>
          <SideBarStyles.Title>{fileDetails?.name}</SideBarStyles.Title>
          <ButtonIcon onClick={() => handleClickAway()}>
            <FaXmark size={25} />
          </ButtonIcon>
        </SideBarStyles.Header>
        <SideBarStyles.ActionsContent>
          {fileDetails?.isFavorite ? (
            <FaHeart
              onClick={() =>
                handleEdit({
                  isFavorite: false,
                })
              }
              size={22}
              color={theme.colors.primary}
            />
          ) : (
            <FaRegHeart
              onClick={() =>
                handleEdit({
                  isFavorite: true,
                })
              }
              size={22}
              color={theme.colors.primary}
            />
          )}
          {canDelete && (
            <FaTrash
              color={theme.colors.primary}
              onClick={() => fileDetails?._id && modalDeleteRef.current?.openModal(fileDetails?._id)}
              size={22}
            />
          )}
        </SideBarStyles.ActionsContent>
        <Input
          small
          label='Nome'
          error={errors?.name}
          control={control}
          type='text'
          name='name'
          Icon={
            canEdit && edit ? (
              <ButtonRound
                style={{
                  backgroundColor: theme.colors.success,
                  minWidth: 25,
                  minHeight: 25,
                  width: 25,
                  height: 25,
                }}
                onClick={handleEditName}
              >
                <FaCheck color='white' size={15} />
              </ButtonRound>
            ) : canEdit ? (
              <ButtonRound
                style={{
                  minWidth: 25,
                  minHeight: 25,
                  width: 25,
                  height: 25,
                }}
                onClick={onEditName}
              >
                <FaPencil color='white' size={13} />
              </ButtonRound>
            ) : null
          }
        />
        <Input
          label='Data'
          error={errors?.createdAt}
          control={control}
          disabled
          small
          Icon={<FaCalendar size={20} />}
          type='text'
          name='createdAt'
        />

        {!fileDetails?.isFolder && (
          <Input
            label='Tamanho do arquivo'
            error={errors?.size}
            control={control}
            disabled
            small
            type='text'
            name='size'
          />
        )}

        <Input label='Quem criou' small control={control} disabled type='text' name='owner' />

        <Input 
          label='Tipo de Documento' 
          small 
          control={control} 
          disabled 
          type='text' 
          name='documentType'
        />

        {fileDetails?.simDataFound && (
          <div
            style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '6px',
              padding: '6px 10px',
              margin: '8px 0 10px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px' }}>🏛️</span>
              <span style={{ fontSize: '11px', color: '#065f46', fontWeight: 600 }}>
                Dados sincronizados com o SIM (TCE-CE)
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleGetData(fileDetails._id, true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#059669',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Recarregar SIM
            </button>
          </div>
        )}

        {!fileDetails?.simDataFound && fileDetails?._id && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0 4px 0' }}>
            <button
              type="button"
              onClick={() => handleGetData(fileDetails._id, true)}
              style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                padding: '4px 8px',
                color: '#334155',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              🔄 Buscar no SIM (TCE-CE)
            </button>
          </div>
        )}

        {metadata.map((meta: any, index: number) => (
          <div key={index}>
            <Label>{meta?.name}</Label>
            <ContentInput gray>
              <Field error={false} name={name} small disabled value={meta.value} />
            </ContentInput>
          </div>
        ))}

        {((fileDetails as any)?.aiStatus === 'completed' || (fileDetails as any)?.aiSummary) && (
          <div style={{ margin: '14px 0', padding: '12px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#065f46' }}>✨ Processado com IA (Gemini)</span>
              {(fileDetails as any)?.aiConfidence && (
                <span style={{ fontSize: '11px', background: '#059669', color: 'white', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                  {Math.round((fileDetails as any).aiConfidence * 100)}% certeza
                </span>
              )}
            </div>
            {(fileDetails as any)?.aiSummary && (
              <p style={{ fontSize: '12px', color: '#047857', margin: 0, lineHeight: '1.4' }}>
                {(fileDetails as any).aiSummary}
              </p>
            )}
            {(fileDetails as any)?.aiJustification && (
              <p style={{ fontSize: '11px', color: '#059669', marginTop: '6px', fontStyle: 'italic', margin: '4px 0 0 0' }}>
                Classificação: {(fileDetails as any).aiJustification}
              </p>
            )}
          </div>
        )}

        {!fileDetails?.isFolder && (
          <div style={{ margin: '14px 0', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>⚖️ Ciclo de Vida Arquivístico (TTD)</span>
              {Boolean((fileDetails as any)?.retentionStatus) && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background:
                    (fileDetails as any).retentionStatus === 'corrente' ? '#dbeafe' :
                    (fileDetails as any).retentionStatus === 'intermediario' ? '#fef3c7' :
                    (fileDetails as any).retentionStatus === 'apto_eliminacao' ? '#fee2e2' :
                    (fileDetails as any).retentionStatus === 'recolhido_permanente' ? '#f3e8ff' : '#f1f5f9',
                  color:
                    (fileDetails as any).retentionStatus === 'corrente' ? '#1d4ed8' :
                    (fileDetails as any).retentionStatus === 'intermediario' ? '#b45309' :
                    (fileDetails as any).retentionStatus === 'apto_eliminacao' ? '#b91c1c' :
                    (fileDetails as any).retentionStatus === 'recolhido_permanente' ? '#6b21a8' : '#475569'
                }}>
                  {(fileDetails as any).retentionStatus === 'corrente' && 'Fase Corrente'}
                  {(fileDetails as any).retentionStatus === 'intermediario' && 'Fase Intermediária'}
                  {(fileDetails as any).retentionStatus === 'apto_eliminacao' && 'Apto para Eliminação'}
                  {(fileDetails as any).retentionStatus === 'recolhido_permanente' && 'Guarda Permanente'}
                  {(fileDetails as any).retentionStatus === 'eliminado' && 'Eliminado'}
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', color: '#475569' }}>
              <div>
                <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px' }}>LIMITE CORRENTE</span>
                <strong>
                  {(fileDetails as any)?.currentPhaseUntil
                    ? format(new Date((fileDetails as any).currentPhaseUntil), 'dd/MM/yyyy')
                    : 'Não calculado'}
                </strong>
              </div>
              <div>
                <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px' }}>LIMITE INTERMEDIÁRIO</span>
                <strong>
                  {(fileDetails as any)?.intermediatePhaseUntil
                    ? format(new Date((fileDetails as any).intermediatePhaseUntil), 'dd/MM/yyyy')
                    : 'Não calculado'}
                </strong>
              </div>
            </div>

            {Boolean((fileDetails as any)?.eliminationLog?.termNumber) && (
              <div style={{ marginTop: '8px', padding: '6px 8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '11px', color: '#991b1b' }}>
                Termo CPAD Nº {(fileDetails as any).eliminationLog.termNumber} em {format(new Date((fileDetails as any).eliminationLog.date), 'dd/MM/yyyy')}
              </div>
            )}
          </div>
        )}

        {!fileDetails?.isFolder && Boolean((fileDetails as any)?.signatures?.length) && (
          <div style={{ margin: '14px 0', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534' }}>🔐 Assinaturas Digitais</span>
              <span style={{ fontSize: '11px', background: '#16a34a', color: 'white', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                {(fileDetails as any).signatures.length} assinada{(fileDetails as any).signatures.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {(fileDetails as any).signatures.map((sig: any, idx: number) => (
                <div key={idx} style={{ fontSize: '11px', color: '#14532d', background: '#ffffff90', padding: '6px 8px', borderRadius: '4px' }}>
                  <div style={{ fontWeight: 600 }}>✓ {sig.signerName}</div>
                  <div style={{ fontSize: '10px', color: '#15803d' }}>
                    {sig.certificateType?.startsWith('govbr') ? 'Gov.br' : 'ICP-Brasil'} • {sig.signedAt ? format(new Date(sig.signedAt), 'dd/MM/yyyy HH:mm') : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            borderBottom: '2px solid #64646420',
          }}
        />

        {fileDetails?._id && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px', marginBottom: '20px', gap: '10px' }}>
            <Label style={{ fontSize: '15px' }}>Etiqueta Física (QR Code)</Label>
            <ButtonIcon 
              onClick={() => window.open(`/print/qrcode/${fileDetails._id}`, '_blank')}
              style={{ width: '100%', padding: '12px', background: theme.colors.primary, color: 'white', borderRadius: '8px', fontSize: '14px', marginTop: '5px' }}
            >
              Imprimir Etiqueta
            </ButtonIcon>
          </div>
        )}

        <div
          style={{
            borderBottom: '2px solid #64646420',
          }}
        />
        {fileDetails?._id && (
          <GroupsSearch
            menuPosition={'absolute'}
            menuPortalTarget={document.querySelector('#portalDocumentDetails') || document.body}
            fileId={[fileDetails?._id]}
          />
        )}
        <ModalDelete
          loading={isLoading}
          title='Deletar Arquivo?'
          ref={modalDeleteRef}
          onSubmitButton={handleSubmitDelete}
        />
      </Content>
    </Container>
  )
}

export default React.forwardRef(FileDetails)
